import React from 'react';
import { useCreateFlowStore } from '@gik/create';
import shallow from 'zustand/shallow';
import { StepItem } from '@gik/ui';
import { Analytics } from '@gik/core/utils';
import { RecipientType } from '@gik/create/enums/RecipientType';
import { useSuggestedProducts } from '@gik/checkout';
import { useInkindSituations } from '@gik/marketing/api/wp/situation';
import { User } from '@gik/core/models/gik/User';
import { IEmailAddressAlreadyTakenResponse, isEmailAddressAlreadyTaken } from '@gik/api/users/auth';
import { isInkindPageRecipientInfoPartiallyComplete } from '@gik/inkind-page/utils/isInkindPageRecipientInfoComplete';
import { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import { IAnalyticsProps } from '@gik/core/utils/Analytics';
import { useUserStore } from '@gik/core/store/UserStore';
import { useUser } from '@gik/api/users/user';
import { CreateFormSteps } from '@gik/create/enums/CreateFormSteps';
import { SituationStepInputNames } from '@gik/create/enums/SituationStepInputNames';
import { AboutStepInputNames } from '@gik/create/enums/AboutStepInputNames';
import { FeatureCareCalendarInputNames } from '@gik/create/enums/FeatureCareCalendarInputNames';
import { FeatureWishlistInputNames } from '@gik/create/enums/FeatureWishlistInputNames';
import { FeatureDonationsInputNames } from '@gik/create/enums/FeatureDonationsInputNames';
import { RecipientInfoStepInputNames } from '@gik/create/enums/RecipientInfoStepInputNames';
import { EmailTakenStatus } from '@gik/create/enums/EmailTakenStatus';
import { PayPalFormInputNames } from '@gik/create/enums/PayPalFormInputNames';
import { GoFundMeFormInputNames } from '@gik/create/enums/GoFundMeFormInputNames';

export enum CreateAnalyticsEvents {
  CREATE_CLICKED = 'CreateClicked',
  CREATE_STARTED = 'CreateStarted',
  CREATE_FINISHED = 'CreateFinished',
  CREATE_HELP_CLICKED = 'CreateHelpClicked',
}

export enum CreateStartedStep {
  ABOUT = 'About',
  SITUATIONS = 'Situation',
  CARE_CALENDAR = 'CareCalendar',
  WISHLIST = 'Wishlist',
  DONATIONS = 'Donations',
  PRIVACY = 'PrivacySettings',
  PAGE = 'PageName',
  RECIPIENT = 'RecipientInfo',
  FINISH = 'Finish',
}

export enum CreateClickedLocation {
  PAGE_HEADER = 'PageHeader',
  HOMEPAGE_TOP = 'HomePageTop',
  HOMEPAGE_HALF = 'HomePageHalf',
  HOMEPAGE_BOTTOM = 'HomePageBottom',
  PAGE_FOOTER = 'PageFooter',
  HOW_IT_WORKS_TOP = 'HowItWorksTop',
  HOW_IT_WORKS_HALF = 'HowItWorksHalf',
  HOW_IT_WORKS_BOTTOM = 'HowItWorksBottom',
  PAGE_FEATURES = 'FeaturesPage',
  COMPETITORS_PAGE = 'CompetitorsPage',
  FAQ_PAGE = 'FaqPage',
  TIPS_SAMPLE_PAGES_PAGE = 'TipsSamplePagesPage',
  BENEFITS_PAGE = 'BenefitsPage',
}

export enum CreateHelpClickedProperties {
  STEP_NUMBER = 'stepNumber',
}

function fireCreateStartedEvent(eventData: IAnalyticsProps, userEmail?: string) {
  Analytics.fireEvent(
    CreateAnalyticsEvents.CREATE_STARTED,
    eventData,
    () => ['stepCompleted'],
    keys => keys.filter(key => key !== 'stepCompleted'),
    userEmail
  );
}

export default function useCreateAnalytics(steps: StepItem<CreateFormSteps>[]) {
  const { stepIndex, subStepIndex, emailTakenStatus, newUserLoginProvider } = useCreateFlowStore(
    state => ({
      stepIndex: state.stepIndex,
      subStepIndex: state.subStepIndex,
      emailTakenStatus: state.emailTakenStatus,
      newUserLoginProvider: state.newUserLoginProvider,
    }),
    shallow
  );

  const formValues = useCreateFlowStore(state => state.formValues);

  const { data: suggestedProducts } = useSuggestedProducts(formValues[SituationStepInputNames.Situations]);
  const { data: situations } = useInkindSituations(1, 100);

  const recipientType: RecipientType = formValues[AboutStepInputNames.RecipientType];
  const selectedSituations: number[] = formValues[SituationStepInputNames.Situations];
  const careCalendarEnabled: boolean = formValues[FeatureCareCalendarInputNames.CareCalendarEnabled];
  const wishlistEnabled: boolean = formValues[FeatureWishlistInputNames.WishlistEnabled];
  const donationsEnabled: boolean = formValues[FeatureDonationsInputNames.DonationsEnabled];
  const privacySettings = {
    // TODO: consider renaming these to be consistent
    allowCrawlers: String(formValues.searchEnginesEnabled),
    allowFlowers: String(formValues.flowersEnabled),
    allowPageSharing: String(formValues.pageSharingEnabled),
    allowPhoneCalls: String(formValues.phoneCallsEnabled),
    allowTexts: String(formValues.textMessagesEnabled),
    allowVisitors: String(formValues.visitorsEnabled),
    canFeature: String(formValues.featurePageEnabled),
    isPublic: String(formValues.showPageEnabled),
  };

  const [clickedTermsOfService, setClickedTermsOfService] = React.useState<boolean>(false);
  const [clickedPrivacyPolicy, setClickedPrivacyPolicy] = React.useState<boolean>(false);
  const [userClickedCareCalendarVideo, setUserClickedCareCalendarVideo] = React.useState<boolean>(false);
  const [userClickedWishlistVideo, setUserClickedWishlistVideo] = React.useState<boolean>(false);
  const [userClickedDonationsVideo, setUserClickedDonationsVideo] = React.useState<boolean>(false);
  const [userClickedPageNameIdeas, setUserClickedPageNameIdeas] = React.useState<boolean>(false);

  const stepIdToAnalyticsStepNameMap = {
    [CreateFormSteps.ABOUT]: CreateStartedStep.ABOUT,
    [CreateFormSteps.SITUATION]: CreateStartedStep.SITUATIONS,
    [CreateFormSteps.FEATURES]: null,
    [CreateFormSteps.PRIVACY]: CreateStartedStep.PRIVACY,
    [CreateFormSteps.PAGE]: null,
    [CreateFormSteps.FINISH]: CreateStartedStep.FINISH,
  };

  const userId = useUserStore(state => state.id);
  const { data: user } = useUser(userId);
  const emailUserId = formValues[AboutStepInputNames.Email] || user?.emailAddress;

  function trackTermsOfService() {
    setClickedTermsOfService(true);
  }

  function trackPrivacyPolicy() {
    setClickedPrivacyPolicy(true);
  }

  function trackIntercomClicked() {
    let currentStepId: CreateFormSteps;

    try {
      currentStepId = steps[stepIndex]?.id;
    } catch (e) {
      console.error(e);
    }

    let stepNumber;

    switch (currentStepId) {
      case CreateFormSteps.FEATURES:
        switch (subStepIndex) {
          case 0:
            stepNumber = CreateStartedStep.CARE_CALENDAR;
            break;
          case 1:
            stepNumber = CreateStartedStep.WISHLIST;
            break;
          case 2:
            stepNumber = CreateStartedStep.DONATIONS;
            break;
        }
        break;
      case CreateFormSteps.PAGE:
        switch (subStepIndex) {
          case 0:
            stepNumber = CreateStartedStep.PAGE;
            break;
          case 1:
            stepNumber = CreateStartedStep.RECIPIENT;
            break;
        }
        break;
      default:
        stepNumber = stepIdToAnalyticsStepNameMap[currentStepId];
    }

    Analytics.fireEvent(
      CreateAnalyticsEvents.CREATE_HELP_CLICKED,
      {
        [CreateHelpClickedProperties.STEP_NUMBER]: stepNumber,
      },
      keys => keys // single key - stepNumber
    );
  }

  function trackCalendarVideoPlayed() {
    setUserClickedCareCalendarVideo(true);
  }

  function trackWishlistVideoPlayed() {
    setUserClickedWishlistVideo(true);
  }

  function trackDonationsVideoPlayed() {
    setUserClickedDonationsVideo(true);
  }

  function trackClickedPageNameIdeas() {
    setUserClickedPageNameIdeas(true);
  }

  function hasRecipientAddedAddress(): boolean {
    const partialInkindPage: Partial<InkindPageAPIModel> = {
      address: formValues.address1 || formValues.address2,
      zip: formValues.postalCode,
      city: formValues.city,
      stateCode: formValues.state,
    };

    const isPartiallyComplete = isInkindPageRecipientInfoPartiallyComplete(partialInkindPage);
    return formValues.includeRecipientAddress == true && isPartiallyComplete;
  }

  async function trackOnFinish(user: User) {
    const currentStepId: CreateFormSteps = steps[stepIndex]?.id;
    const userTypeInfo: {
      userType?: string;
      emailPassword?: string;
      googleOAuth?: string;
      facebookOAuth?: string;
    } = {};
    let _emailTakenStatus: IEmailAddressAlreadyTakenResponse;

    if (currentStepId === CreateFormSteps.PAGE) {
      userTypeInfo.userType = 'ExistingUser';

      _emailTakenStatus = await isEmailAddressAlreadyTaken(user.id);

      if (_emailTakenStatus.facebook) {
        userTypeInfo.facebookOAuth = 'true';
      } else if (_emailTakenStatus.google) {
        userTypeInfo.googleOAuth = 'true';
      } else {
        userTypeInfo.emailPassword = 'true';
      }

      if (subStepIndex === 0) {
        fireCreateStartedEvent({
          stepCompleted: CreateStartedStep.PAGE,
          clickedPageNameIdeas: String(userClickedPageNameIdeas),
        });
      }

      fireCreateStartedEvent({
        stepCompleted: CreateStartedStep.RECIPIENT,
        recipientAddedAsPO: String(formValues[RecipientInfoStepInputNames.AddAsPageOrganizer]),
        recipientAddressAdded: String(hasRecipientAddedAddress()),
      });
    } else {
      // FIXME: duplicated calculation of userTypeInfo (see above)
      switch (emailTakenStatus) {
        case EmailTakenStatus.FREE:
          userTypeInfo.userType = 'NewUser';
          break;
        case EmailTakenStatus.GIK:
          userTypeInfo.userType = 'ExistingUser';
          userTypeInfo.emailPassword = 'true';
          break;
        case EmailTakenStatus.GOOGLE:
          userTypeInfo.userType = 'ExistingUser';
          userTypeInfo.googleOAuth = 'true';
          break;
        case EmailTakenStatus.FACEBOOK:
          userTypeInfo.userType = 'ExistingUser';
          userTypeInfo.facebookOAuth = 'true';
          break;
      }
    }

    fireCreateStartedEvent({
      stepCompleted: CreateStartedStep.FINISH,
      ...userTypeInfo,
      ...(newUserLoginProvider ? { newUserLoginProvider } : {}),
    });
  }

  // STEP COMPLETED events
  React.useEffect(() => {
    if (!steps || stepIndex === undefined) {
      return;
    }

    const donationInfoProvided = [];
    let currentStepId: CreateFormSteps;

    try {
      currentStepId = steps[stepIndex - 1]?.id;
    } catch (e) {
      console.error(e);
    }

    switch (currentStepId) {
      case CreateFormSteps.ABOUT:
        fireCreateStartedEvent(
          {
            stepCompleted: CreateStartedStep.ABOUT,
            recipientType,
            clickedTermsOfService: String(clickedTermsOfService),
            clickedPrivacyPolicy: String(clickedPrivacyPolicy),
          },
          emailUserId
        );
        break;
      case CreateFormSteps.SITUATION:
        fireCreateStartedEvent({
          stepCompleted: CreateStartedStep.SITUATIONS,
          situationIDs: selectedSituations.join(','),
          situationNames: situations
            .filter(situation => selectedSituations.includes(situation.id))
            .map(situation => situation.name)
            .join(','),
        });
        break;
      case CreateFormSteps.FEATURES: // if this index changed this means user just finished DONATIONS
        if (formValues[FeatureDonationsInputNames.DonationsValues]?.[PayPalFormInputNames.EmailAddress]) {
          donationInfoProvided.push('PayPal');
        }

        if (formValues[FeatureDonationsInputNames.DonationsValues]?.[GoFundMeFormInputNames.PageURL]) {
          donationInfoProvided.push('GoFundMe');
        }

        fireCreateStartedEvent({
          stepCompleted: CreateStartedStep.DONATIONS,
          donationsVideoPlayed: String(userClickedDonationsVideo),
          donationsEnabled: String(donationsEnabled),
          donationInfoProvided: donationInfoProvided.join(','),
        });

        break;
      case CreateFormSteps.PRIVACY:
        fireCreateStartedEvent({
          stepCompleted: CreateStartedStep.PRIVACY,
          ...privacySettings,
        });
        break;
      case CreateFormSteps.PAGE:
        switch (recipientType) {
          case RecipientType.Myself: // if this index changed as MYSELF this means user just finished PAGE
            fireCreateStartedEvent({
              stepCompleted: CreateStartedStep.PAGE,
              clickedPageNameIdeas: String(userClickedPageNameIdeas),
            });
            // fire RECIPIENT step related events too
            fireCreateStartedEvent({
              stepCompleted: CreateStartedStep.RECIPIENT,
              recipientAddedAsPO: String(formValues[RecipientInfoStepInputNames.AddAsPageOrganizer]),
              recipientAddressAdded: String(hasRecipientAddedAddress()),
            });
            break;
          default:
            // if this index changed NOT as MYSELF this means user just finished RECIPIENT
            fireCreateStartedEvent({
              stepCompleted: CreateStartedStep.RECIPIENT,
              recipientAddedAsPO: String(formValues[RecipientInfoStepInputNames.AddAsPageOrganizer]),
              recipientAddressAdded: String(hasRecipientAddedAddress()),
            });
        }
        break;
      case CreateFormSteps.FINISH:
        fireCreateStartedEvent({
          stepCompleted: CreateStartedStep.FINISH,
        });
        break;
    }
  }, [stepIndex]);

  // Sub Step completed events
  React.useEffect(() => {
    if (!steps || stepIndex === undefined) {
      return;
    }

    let currentStepId: CreateFormSteps;
    try {
      currentStepId = steps[stepIndex]?.id;
    } catch (e) {
      console.error(e);
    }

    switch (currentStepId) {
      case CreateFormSteps.FEATURES:
        switch (subStepIndex - 1) {
          case 0:
            fireCreateStartedEvent({
              stepCompleted: CreateStartedStep.CARE_CALENDAR,
              careCalendarEnabled: String(careCalendarEnabled),
              calendarVideoPlayed: String(userClickedCareCalendarVideo),
            });
            break;
          case 1:
            fireCreateStartedEvent({
              stepCompleted: CreateStartedStep.WISHLIST,
              wishlistEnabled: String(wishlistEnabled),
              wishlistVideoPlayed: String(userClickedWishlistVideo),
              userRemovedWishlistItemIDs: suggestedProducts
                ? suggestedProducts
                    .filter(product => !formValues[FeatureWishlistInputNames.WishlistItems]?.includes(product.id))
                    .map(product => product.id)
                    .join(',')
                : '',
            });

            break;
        }
        break;
      case CreateFormSteps.PAGE:
        switch (recipientType) {
          case RecipientType.Myself:
            break;
          default:
            switch (subStepIndex - 1) {
              case 0:
                fireCreateStartedEvent({
                  stepCompleted: CreateStartedStep.PAGE,
                  clickedPageNameIdeas: String(userClickedPageNameIdeas),
                });
                break;
            }
        }
        break;
    }
  }, [subStepIndex]);

  return {
    trackOnFinish,
    trackTermsOfService,
    trackPrivacyPolicy,
    trackIntercomClicked,
    trackCalendarVideoPlayed,
    trackWishlistVideoPlayed,
    trackDonationsVideoPlayed,
    trackClickedPageNameIdeas,
    ...useCreateAnalyticsController(),
  };
}

export function useCreateAnalyticsController() {
  function trackCreateClicked(location: CreateClickedLocation) {
    Analytics.fireEvent(
      CreateAnalyticsEvents.CREATE_CLICKED,
      {
        location,
      },
      keys => keys // only one key - location
    );
  }

  return {
    trackCreateClicked,
  };
}
